import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { CronJob, CronExecution, QueueStatus, CreateCronJobRequest, UpdateCronJobRequest } from '../types/cron.js';
import * as cronApi from './cron-api.js';
import { wsClient } from './websocket-client.js';

export type CronLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedJobs: CronJob[] = [];
let sharedSelectedJob: CronJob | null = null;
let sharedExecutions: CronExecution[] = [];
let sharedQueueStatus: QueueStatus | null = null;
let sharedLoadingState: CronLoadingState = 'idle';
let sharedError: string | null = null;

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadJobsInternal(): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    const [jobs, queueStatus] = await Promise.all([
      cronApi.getJobs(),
      cronApi.getQueueStatus(),
    ]);
    sharedJobs = jobs;
    sharedQueueStatus = queueStatus;
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Cron-Jobs konnten nicht geladen werden';
  }

  notifyHosts();
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string };
  const type = detail.type;

  if (
    type === 'cron:job-started' ||
    type === 'cron:job-completed' ||
    type === 'cron:job-failed' ||
    type === 'cron:queue-updated' ||
    type === 'cron:job-created' ||
    type === 'cron:job-updated' ||
    type === 'cron:job-deleted'
  ) {
    void loadJobsInternal();

    // Reload executions if a job is selected and the event is execution-related
    if (
      sharedSelectedJob &&
      (type === 'cron:job-started' || type === 'cron:job-completed' || type === 'cron:job-failed')
    ) {
      void cronApi.getExecutions(sharedSelectedJob.id).then(execs => {
        sharedExecutions = execs;
        notifyHosts();
      }).catch((err) => { console.warn('Failed to reload executions:', err); });
    }
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

export class CronStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  // ─── Getters ────────────────────────────────────────────────────────

  get jobs(): CronJob[] {
    return sharedJobs;
  }

  get selectedJob(): CronJob | null {
    return sharedSelectedJob;
  }

  get executions(): CronExecution[] {
    return sharedExecutions;
  }

  get queueStatus(): QueueStatus | null {
    return sharedQueueStatus;
  }

  get loadingState(): CronLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  // ─── Load ───────────────────────────────────────────────────────────

  async loadJobs(): Promise<void> {
    await loadJobsInternal();
  }

  // ─── CRUD Operations ───────────────────────────────────────────────

  async createJob(data: CreateCronJobRequest): Promise<CronJob> {
    const job = await cronApi.createJob(data);
    await loadJobsInternal();
    return job;
  }

  async updateJob(id: number, data: UpdateCronJobRequest): Promise<CronJob> {
    const job = await cronApi.updateJob(id, data);
    await loadJobsInternal();
    if (sharedSelectedJob?.id === id) {
      sharedSelectedJob = job;
      notifyHosts();
    }
    return job;
  }

  async deleteJob(id: number): Promise<void> {
    await cronApi.deleteJob(id);
    if (sharedSelectedJob?.id === id) {
      sharedSelectedJob = null;
      sharedExecutions = [];
    }
    await loadJobsInternal();
  }

  // ─── Job Actions ──────────────────────────────────────────────────

  async triggerJob(id: number): Promise<void> {
    await cronApi.triggerJob(id);
  }

  async toggleJob(id: number): Promise<void> {
    const updated = await cronApi.toggleJob(id);
    if (sharedSelectedJob?.id === id) {
      sharedSelectedJob = updated;
    }
    await loadJobsInternal();
  }

  // ─── Executions ───────────────────────────────────────────────────

  async loadExecutions(jobId: number, limit?: number): Promise<void> {
    try {
      sharedExecutions = await cronApi.getExecutions(jobId, limit);
      notifyHosts();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Executions konnten nicht geladen werden';
      notifyHosts();
    }
  }

  // ─── Selection ────────────────────────────────────────────────────

  selectJob(job: CronJob): void {
    sharedSelectedJob = job;
    notifyHosts();
    void this.loadExecutions(job.id);
  }

  clearSelection(): void {
    sharedSelectedJob = null;
    sharedExecutions = [];
    notifyHosts();
  }

  // ─── Refresh ──────────────────────────────────────────────────────

  async refresh(): Promise<void> {
    await loadJobsInternal();
    if (sharedSelectedJob) {
      void this.loadExecutions(sharedSelectedJob.id);
    }
  }
}
